import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    inject,
    Input,
    NgModuleRef,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientAdminLoaderService } from '@icms/client/admin-loader/icms-client-admin-loader.service';
import { ICmsContent } from '@icms/core/content';
import { ICmsContentComponentValidationService } from '@icms/core/content/component/icms-content-component-validation.service';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { NICmsContentComponent } from '@icms/core/content/interfaces/icms-content-component.interfaces';
import { ICmsCoreIntegrationOutletService } from '@icms/core/integration/icms-core-integration-outlet.service';
import { ICmsUtilViewContainerDirective } from '@icms/core/util/view-container/icms-util-view-container.directive';
import { BehaviorSubject, merge, Subject, Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'icms-outlet',
    templateUrl: 'icms-outlet.html',
    styleUrls: ['icms-outlet.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NICmsContentComponent.OutletInstance,
            useFactory: (outlet: ICmsOutletComponent) => {
                return outlet;
            },
            deps: [ICmsOutletComponent]
        }
    ]
})
export class ICmsOutletComponent extends UiComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * Components data entries
     */
    @Input()
    @ICmsContent({
        watchDepth: 0
    })
    components?: NICmsContentComponent.IData[];

    /**
     * Components identifieres list, extends default components list by requested ones here even if they are on demand only
     */
    @Input()
    allow?: string[];

    @Input()
    @HostBinding('class._fullHight')
    fullHeight = false;

    /**
     * Components identifieres list, restricts the list to these components only
     */
    @Input()
    allowOnly?: string[];

    /**
     * Components identifieres list, disallows listed components
     */
    @Input()
    disallow?: string[];

    /**
     * Main component will be a target for component adding
     * without a need to select it
     */
    @Input()
    main: boolean = false;

    /**
     * Outlet description used in icms
     */
    @Input({ transform: (value: string | undefined) => value || 'Name not set' })
    name?: string;

    @Input()
    options?: NICmsContentComponent.IOutletOptions;

    readonly optionsChange = new Subject<void>();

    /**
     * Currnetly visible components
     */
    printableComponents$ = new BehaviorSubject<NICmsContentComponent.IData[]>([]);

    @ViewChild(ICmsUtilViewContainerDirective, { static: true })
    viewContainerTemplate!: ICmsUtilViewContainerDirective;

    /**
     * Tracking map
     */
    private trackMap = new Map<NICmsContentComponent.IData, number>();

    /**
     * Components validation subcription
     */
    private validationSubscription?: Subscription;

    private iCmsContentService = inject(ICmsContentService);
    protected iCmsCoreIntegrationOutletService = inject(ICmsCoreIntegrationOutletService);
    private iCmsClientAdminLoaderService = inject(ICmsClientAdminLoaderService);
    readonly ngModuleRef = inject(NgModuleRef);
    private iCmsContentComponentValidationService = inject(ICmsContentComponentValidationService);

    override ngOnInit() {
        this.subscriptions.add(this.iCmsClientAdminLoaderService.state.subscribe(() => this.detectChanges()));
        this.updatePrintableComponents();
        super.ngOnInit();
        this.iCmsCoreIntegrationOutletService.registerEntry(this);
    }

    override ngOnDestroy() {
        this.iCmsCoreIntegrationOutletService.unregisterEntry(this);
        this.validationSubscription?.unsubscribe();
        super.ngOnDestroy();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes['options'] && this.initiated) {
            this.optionsChange.next();
        }
    }

    get printableComponents(): NICmsContentComponent.IData[] {
        return this.printableComponents$.value;
    }

    override detectChanges(): void {
        if (this.initiated) {
            this.updatePrintableComponents();
        }
        super.detectChanges();
    }

    canPrintComponent(componentId: string): boolean {
        if (this.disallow?.includes(componentId)) {
            return false;
        }
        if (this.allowOnly) {
            return this.allowOnly.includes(componentId);
        }
        const componentMeta = this.iCmsContentService.getComponentMeta(componentId);
        if (!componentMeta || (componentMeta.onDemandOnly && !this.allow?.includes(componentId))) {
            return false;
        }
        return true;
    }

    private updatePrintableComponents() {
        let components = this.components?.filter(componentData => componentData && this.canPrintComponent(componentData.id)) || [];

        this.validationSubscription?.unsubscribe();

        // outside of icms skip non-printable components
        if (this.canFilterByValidation) {
            if (components.length) {
                let busy = true;
                let cb = this.timeoutService.debounce(() => {
                    this.detectChanges();
                }, 50);
                // subscribe to state changes to trigger update
                this.validationSubscription = merge(
                    ...components.map(component => this.iCmsContentComponentValidationService.getState$(component, undefined, this))
                ).subscribe(() => {
                    if (!busy) {
                        cb();
                    }
                });
                busy = false;

                // filter out non-valid components
                components = components.filter(
                    componentData => !this.iCmsContentComponentValidationService.getState$(componentData, undefined, this).getValue()
                );
            }
        }
        const currentComponents = this.printableComponents$.value;
        if (
            !currentComponents ||
            currentComponents.length !== components.length ||
            currentComponents.find((i, index) => components[index] !== i)
        ) {
            // code below is to optimize tracking
            // we do not want to change tracking key if object is the same as it was => no destroy+new components on position swap
            // we do not want to change tracking key if we have any free key => no  destroy+new components when multiple change (we are able to update them)
            // we only want to add new ones when we need to, when number of components increases
            // this way we keep components objects, so no destruction and we things dont blik, edit in icms trays dont close
            const matchingIndexes: number[] = [];
            const missingComponents: NICmsContentComponent.IData[] = [];
            components.forEach(component => {
                const trackIndex = this.trackMap.get(component);
                trackIndex ? matchingIndexes.push(trackIndex) : missingComponents.push(component);
            });
            let trackMapMax = 0;
            const freeIndexes: number[] = [];
            this.trackMap.forEach((index, component) => {
                if (!matchingIndexes.includes(index)) {
                    this.trackMap.delete(component);
                    freeIndexes.push(index);
                }
                trackMapMax = Math.max(trackMapMax, index);
            });
            missingComponents.forEach(component => this.trackMap.set(component, freeIndexes.shift() || ++trackMapMax));

            // informa about the change
            this.printableComponents$.next(components);
        }
    }

    private get canFilterByValidation() {
        return !this.client.isCms;
    }

    protected componentTrackFn = (index: number, component: NICmsContentComponent.IData) => {
        return this.trackMap.get(component);
    };
}
